// React
import * as React from "react";
import _ from 'lodash';


// Internal components
import type { IWidgetDataValidateResp } from '@UI/Widgets/IWidgetDataValidateResp';

import { IRadioButtons } from '../Control/CommonList';
import * as WidgetsThingsStructs from "../WidgetsThingsStructs";
import { ColorPaletteMode } from './ColorPalette';

import { getRandomSmallKey, isDefined, IsNullOrEmpty, IsNullOrWhiteSpace } from "@Utils/Micro";

import { IWidgetThingsClass } from "../Utils/IWidgetThingsClass";
import { LazyLoadingAnimation } from '@Primitive/Lazy';
import { WebThing, WebThingPort } from '@API/WebThing';
import { Plegma } from '@API/Api';
import { WebThingPortType } from '@API/WebNodeTypes';
import { WebNode } from '@API/WebNode';
import { IWidgetDescription, WidgetDescriptionToPlegmaThing } from '@UI/Widgets/WidgetStructs';
import { DateFormat, TimeFormat } from '@Utils/Formats';

const Form = React.lazy(() => import("./FormAsync"));
const FormWidgetSettings = React.lazy(() => import("./FormSettingsAsync"));

import type { FormData, FormElementSettings, ISelectData, FormSettings } from "./FormStruct";
import {
    FormPortKeys, _getLastYLayoutPosition, ElementType, IFormat, FormGroupLayout,
    FormZoomPolicy, FormOptionsJsonSchemeValidator, ButtonThemeType
} from "./FormStruct";
// =======================================================================================================

// ------------------------------------------------------------------------------------------------------
/**
 * Migrates widget's data.
 *
 * @param {FormData} widgetData
 * @returns {FormData}
 */
function migrateFormWidgetData(widgetData: FormData): FormData {
    let canvas = widgetData.canvasLayoutDesc;
    const canvasExists = isDefined(canvas);

    // Set the key of each form element
    _.forEach(widgetData.FormSettings.Elements, (el) => {
        if (!isDefined(el.Key))
            el.Key = el.JsonName;

        // Set IsHidden field to false if it's not defined.
        if (!isDefined(el.IsHidden))
            el.IsHidden = false;

        if (!isDefined(el.GroupKey))
            el.GroupKey = "";

    });

    // Update Select data with correct keys
    _.forEach(widgetData.FormSettings.SelectData, (s) => {
        let element = _.find(
            widgetData.FormSettings.Elements,
            (el) => el.Key == s.Key
        );
        if (element == undefined) {
            element = _.find(
                widgetData.FormSettings.Elements,
                (el) => el.JsonName == s.JsonName
            );
            if (element != undefined && element.Key != s.Key) {
                // In case there is already a canvas item,
                // add a new copy of it with the updated key.
                if (canvasExists) {
                    let item = canvas[element.Key];
                    if (item != undefined) {
                        item.Key = s.Key;
                        canvas[s.Key] = item;
                        delete canvas[element.Key];
                    }
                }
                // Update element's key.
                element.Key = s.Key;
            }
        }

        // add value to `FormSettings.SelectData[].data[].key`
        _.forEach(s.data, (selectData, i) => {

            if (!isDefined(selectData.key))
                selectData.key = i + "_" + getRandomSmallKey();
        });

        // default is true
        if (s.closeMenuOnSelect == undefined)
            s.closeMenuOnSelect = true;
    });

    // initialize RadioButtons
    if (!isDefined(widgetData.FormSettings.RadioButton))
        widgetData.FormSettings.RadioButton = [];

    // Update RadioButtons
    _.forEach(widgetData.FormSettings.Elements, (el) => {
        if (el.Type == ElementType.RadioButtons) {
            let key;
            let radio = _.find(
                widgetData.FormSettings.RadioButton,
                (r) => r.key == el.Key
            );

            if (radio == undefined) {
                // We need to create the radio button
                let r: IRadioButtons = _.cloneDeep(el.DefaultValue);
                r.key = el.Key;
                key = r.key;
                widgetData.FormSettings.RadioButton.push(r);
            }
            else
                key = radio.key;

            if (el.Key != key) {
                // In case there is already a canvas item,
                // add a new copy of it with the updated key.
                if (canvasExists) {
                    let item = canvas[el.Key];
                    if (item != undefined) {
                        item.Key = key;
                        canvas[key] = item;
                        delete canvas[el.Key];
                    }
                }
                // Update element's key.
                el.Key = key;
            }
        }
    });

    // Clean radio buttons from old radio buttons
    _.pullAll(widgetData.FormSettings.RadioButton, _.filter(widgetData.FormSettings.RadioButton, (r: IRadioButtons) => !isDefined(r.key)));

    // add value to `FormSettings.RadioButton[].buttons[].key`
    _.forEach(widgetData.FormSettings.RadioButton, (radio) => {
        _.forEach(radio.buttons, (radioButton, i) => {
            if (!isDefined(radioButton.key))
                radioButton.key = i + "_" + getRandomSmallKey();
        });
    })

    if (!canvasExists) {
        widgetData.canvasLayoutDesc = {};

        // Set layout for elements.
        _.forEach(widgetData.FormSettings.Elements, (element, index) => {
            widgetData.canvasLayoutDesc[element.Key] = {
                Key: element.Key,
                X: 0,
                Y: index * 4,
                W: 6,
                H: 4,
                MinH: 1,
                MinW: 1,
                IsDraggable: true,
                IsResizable: true
            };
        });

        // Set layout for buttons.
        let totalElements: number = widgetData.FormSettings.Elements.length;
        _.forEach(widgetData.FormSettings.Buttons, (button, index) => {
            widgetData.canvasLayoutDesc[button.Key] = {
                Key: button.Key,
                X: 0,
                Y: totalElements * 4 * (index + 1),
                W: 2,
                H: 2,
                MinH: 1,
                MinW: 1,
                IsDraggable: true,
                IsResizable: true
            };
        });
    } else {
        let migrate: boolean = true;

        // check if 1rst widget; has the new `minH` and `minW` values
        for (let key in canvas) {
            if (canvas[key].MinH === 1) {
                // no need to migrate
                migrate = false;
            }
            break;
        }
        if (migrate) {
            _.forOwn(canvas, (desc) => {
                // reset max width/height
                desc.MaxH = undefined;
                desc.MaxW = undefined;
                //
                desc.MinH = 1;
                desc.MinW = 1;
            });
        }
    }

    // assign `Color` field at each button (if not defined)
    if (widgetData.FormSettings.Buttons.length > 0 &&
        IsNullOrEmpty(widgetData.FormSettings.Buttons[0].Color)) {
        widgetData.FormSettings.Buttons.forEach((btn) => {
            btn.Color = 'rgb(0, 188, 212)';
        })
    }
    if (widgetData.FormSettings.Buttons.length > 0 &&
        IsNullOrEmpty(widgetData.FormSettings.Buttons[0].FontColor)) {
        widgetData.FormSettings.Buttons.forEach((btn) => {
            if (btn.Flat)
                btn.FontColor = btn.Color;
            else
                btn.FontColor = 'white';
        })
    }

    // find missing elements from canvas
    let missingElements = _.filter(widgetData.FormSettings.Elements, (element: FormElementSettings) => !_.has(widgetData.canvasLayoutDesc, element.Key));
    if (missingElements.length > 0) {
        // Add any missing elements
        _.forEach(missingElements, (el) => {
            widgetData.canvasLayoutDesc[el.Key] = {
                Key: el.Key,
                X: 0,
                Y: _getLastYLayoutPosition(widgetData.canvasLayoutDesc),
                W: 2,
                H: 2,
                MinH: 1,
                MinW: 1,
                IsDraggable: true,
                IsResizable: true
            };
        });

        // Fill any empty spaces
        let minY = Number.MAX_VALUE;
        _.forOwn(widgetData.canvasLayoutDesc, (el) => {
            if (el.Y < minY)
                minY = el.Y;
        });
        if (minY > 0) {
            _.forOwn(widgetData.canvasLayoutDesc, (el) => {
                el.Y -= minY;
            });
        }
    }

    // Initialize image transformation (if needs to).
    _.forEach(widgetData.FormSettings.Image, v => {
        if (!isDefined(v.Transformation))
            v.Transformation = {
                MaxWidth: 1024,
                MaxHeight: 1024,
                Format: IFormat.JPEG,
                Quality: 0.92
            };
    });

    // Group support
    _.forEach(_.groupBy(widgetData.FormSettings.Elements, x => x.GroupKey), (g, k) => {
        if (!IsNullOrWhiteSpace(k)) {
            // be in the canvas layout we don't have any actual elements
            _.forEach(g, x => {
                if (_.has(widgetData.canvasLayoutDesc, x.Key)) {
                    delete widgetData.canvasLayoutDesc[x.Key];
                }
            });

            // check if the canvas is exist
            let layout = widgetData.GroupCanvasLayoutDesc[k];
            if (!isDefined(layout)) {
                widgetData.GroupCanvasLayoutDesc[k] = {};
                // Set layout for elements.
                _.forEach(g, (element, index) => {
                    widgetData.GroupCanvasLayoutDesc[k][element.Key] = {
                        Key: element.Key,
                        X: 0,
                        Y: index * 4,
                        W: 6,
                        H: 4,
                        MinH: 1,
                        MinW: 1,
                        IsDraggable: true,
                        IsResizable: true
                    };
                });
            }
        }
    });

    if (!isDefined(widgetData.FormSettings.GroupLayout))
        widgetData.FormSettings.GroupLayout = FormGroupLayout.List;

    // Check if all groups have layout
    if (!isDefined(widgetData.GroupCanvasLayoutDesc))
        widgetData.GroupCanvasLayoutDesc = {};
    _.forEach(widgetData.FormSettings.Groups, g => {
        if (!isDefined(g.UserKey))
            g.UserKey = g.Key;

        if (!isDefined(widgetData.GroupCanvasLayoutDesc[g.Key]))
            widgetData.GroupCanvasLayoutDesc[g.Key] = {};
    });

    return widgetData;
}
// =======================================================================================================
// Form Description interface
import { SELECT_ALL_LABEL } from '@Primitive/SelectLocalization';
export class FormWidget extends IWidgetThingsClass<FormData> {
    // ------------------------------------------------------------------------------------------------
    public Render(key: string, width: number, height: number): React.ReactNode {
        return <WidgetsThingsStructs.LazyWidget >
            <Form />
        </WidgetsThingsStructs.LazyWidget>
    }
    // ------------------------------------------------------------------------------------------------
    public RenderWidgetSettings(key: string, width: number, height: number): React.ReactElement<any> {
        const parent = this.Parent;
        return <React.Suspense fallback={<LazyLoadingAnimation />}>
            <FormWidgetSettings
                key={"Settings-" + key}
                width={width}
                height={height}
                WidgetData={_.cloneDeep(this.WidgetData)} // copy of the data and is return the copy in the update cb
                WidgetDefaultValues={_.cloneDeep(this.Description.DefaultValues)}
                onWidgetDataUpdate={(d) => this.WidgetDataUpdate(d)}
                ThingKey={this.ThingKey}
                NodeKey={this.NodeKey}
                SettingListToolbar={isDefined(parent) ? parent.SettingsListToolbar : null}
            />
        </React.Suspense>
    }
    // ------------------------------------------------------------------------------------------------
    public ValidateValues(key: string, value: any): IWidgetDataValidateResp {
        let isSelectData = _.findIndex(this.WidgetData.FormSettings.SelectData, x => x.Key == key) > -1 ||
            _.findIndex(this.WidgetData.FormSettings.RadioButton, x => x.key == key) > -1;
        if (isSelectData)
            return FormOptionsJsonSchemeValidator().DataValidateString(value);
        return { valid: true, errors: '' };
    }
    // ------------------------------------------------------------------------------------------------
    private getPorts(settings: FormSettings): WebThingPort[] {
        let ports: WebThingPort[] = [];

        if (settings != null) {
            // One port per button element.
            ports = _.map(settings.Buttons, btn => {
                return new WebThingPort({
                    Name: btn.Label,
                    PortKey: btn.Key,
                    Type: Plegma.ePortType.JsonString,
                    ioDirection: Plegma.ioPortDirection.Output,
                    ConfFlags: Plegma.ePortConf.None,
                    Description: "Outputs the form\'s data",
                    State: ""
                }, WebThingPortType.Trigger);
            });

            ports = _.concat(
                ports,
                // One port per select element.
                _.map(settings.SelectData, (v: ISelectData) => {
                    return new WebThingPort(
                        {
                            Name: v.JsonName,
                            PortKey: v.Key,
                            Type: Plegma.ePortType.JsonString,
                            ioDirection: Plegma.ioPortDirection.Input,
                            ConfFlags: Plegma.ePortConf.None,
                            Description: "Set custom options to select element",
                            State: ""
                        },
                        WebThingPortType.Trigger,
                        (data) => this.ValidateValues(v.Key, data));
                }),
                // One port per radio buttons element.
                _.map(settings.RadioButton, (v: IRadioButtons) => {
                    // Get JSON name from element.
                    const el = _.find(
                        settings.Elements,
                        (v_: FormElementSettings) => { return v_.Key == v.key; }
                    );
                    return new WebThingPort(
                        {
                            Name: isDefined(el) ? el.JsonName : v.key,
                            PortKey: v.key,
                            Type: Plegma.ePortType.JsonString,
                            ioDirection: Plegma.ioPortDirection.Input,
                            ConfFlags: Plegma.ePortConf.None,
                            Description: 'Set custom options to radio buttons element',
                            State: ''
                        },
                        WebThingPortType.Trigger,
                        (data) => this.ValidateValues(v.key, data));
                }));

            // Port for filling all form fields.
            ports.push(new WebThingPort(
                {
                    Name: "Fill Values",
                    PortKey: FormPortKeys.fillValues,
                    Type: Plegma.ePortType.JsonString,
                    ioDirection: Plegma.ioPortDirection.Input,
                    ConfFlags: Plegma.ePortConf.None,
                    Description: "Fill all form\'s fields with custom values",
                    State: ""
                },
                WebThingPortType.Trigger));

            // On select change event port.
            ports.push(new WebThingPort(
                {
                    Name: 'On Select Change',
                    PortKey: FormPortKeys.onSelectChange,
                    Type: Plegma.ePortType.JsonString,
                    ioDirection: Plegma.ioPortDirection.Output,
                    ConfFlags: Plegma.ePortConf.None,
                    Description: 'Outputs the JSON name and data of the changed select element and the overall form\'s data',
                    State: ''
                }, WebThingPortType.Trigger));
        }

        return ports;
    }
    // ------------------------------------------------------------------------------------------------
    updateThing() {
        let webNode = WebNode.GetInstanceKey(this.NodeKey);
        let thing = webNode.GetThing(this.ThingKey);

        let settings: FormSettings = _.cloneDeep(this.WidgetData.FormSettings);
        if (settings != null) {
            // Clean old ports
            thing.Ports = {};
            _.forEach(this.getPorts(settings), (port) => {
                thing.AddPort(port, false);
            });
        }

        webNode.GetModule(this.WebModuleKey).UpdateThing(thing);
    }
    // ------------------------------------------------------------------------------------------------
    initThing() {
        // Thing description
        let plegmaThing = WidgetDescriptionToPlegmaThing(this.Description);

        // Ports
        let settings: FormSettings = _.cloneDeep(this.WidgetData.FormSettings);
        let ports: WebThingPort[] = this.getPorts(settings);

        // Create the thing
        let thing = new WebThing(this.WebModule, plegmaThing, ports);
        this.ThingKey = thing.ThingKey;

        // Create the JSMacros prototype
        if (!_.has(this.Description.JSMacros, "onChangeUpdate"))
            this.Description.JSMacros["onChangeUpdate"] = {
                Name: "onChangeUpdate",
                Description: "Macro to calculate fields values when we have change in values",
                Enable: false,
                Code: "",
                IsValid: false
            };
    }
    // ------------------------------------------------------------------------------------------------
    public static GetDefaultWidgetDataStatic(): FormData {
        return _.cloneDeep({
            FormSettings: {
                Groups: [],
                Buttons: [],
                Elements: [],
                SelectData: [],
                RadioButton: [],
                ColorPaletteMode: ColorPaletteMode.Sketch,
                FormZoomPolicy: FormZoomPolicy.FillWidth,
                Format: DateFormat.ISO,
                BorderRadius: 8,
                TimeFormat: TimeFormat.short,
                Colors: {
                    Mandatory: "black",
                    Default: "black",
                    ReadOnly: "black",
                    InputField: "black",
                    Placeholder: "rgba(0, 0, 0, 0.3)",
                    FocusedUnderline: "rgb(0, 188, 212)"
                },
                Image: [],
                BoldHeaderText: true
            } as FormSettings,
            canvasLayoutDesc: {},
            GroupCanvasLayoutDesc: {},
            layoutThres: 450
        });
    }
    // ------------------------------------------------------------------------------------------------
    public GetDefaultWidgetData(): FormData {
        return FormWidget.GetDefaultWidgetDataStatic();
    }
    // ------------------------------------------------------------------------------------------------
    public static GetWidgetDesc(): IWidgetDescription {
        let jsonName: string = 'elem_' + getRandomSmallKey();
        let buttonKey: string = getRandomSmallKey();

        let data: FormData = FormWidget.GetDefaultWidgetDataStatic();
        data.FormSettings.Elements = [{
            GroupKey: null,
            Key: jsonName,
            JsonName: jsonName,
            TooltipLabel: '',
            Label: 'New element',
            Placeholder: 'Type...',
            Type: ElementType.Text,
            Mandatory: true,
            ReadOnly: false,
            ValidationError: false,
            DuplicateJsonName: false,
            Dependency: [],
            IsHidden: false,
            DefaultValue: null
        }];
        data.FormSettings.Buttons = [{
            Label: 'View',
            Key: buttonKey,
            Type: ButtonThemeType.Primary,
            Flat: true,
            Color: 'rgb(0, 188, 212)',
            FontColor: 'black',
            ForceSend: false,
            Uppercase: true
        }];
        data.canvasLayoutDesc = {
            [jsonName]: {
                Key: jsonName,
                X: 0,
                Y: 0,
                W: 6,
                H: 4,
                MinH: 1,
                MinW: 1,
                IsDraggable: true,
                IsResizable: true
            },
            [buttonKey]: {
                Key: buttonKey,
                X: 0,
                Y: 4,
                W: 2,
                H: 2,
                MinH: 1,
                MinW: 1,
                IsDraggable: true,
                IsResizable: true
            }
        };

        return {
            Title: "Form",
            WidgetTitle: "",
            Key: "FormWidget",
            Type: "FormWidget",
            Data: data,
            Description: "Form widget",
            MinH: 1,
            MinW: 1,
            ShowExtraMenu: false,
            IconURI: WidgetsThingsStructs.IconURIPath + "/png/forms.png"
        };
    }
    // ------------------------------------------------------------------------------------------------
    public MigrateDefaultWidgetData(data: FormData): FormData {
        return migrateFormWidgetData(data);
    }
    // ------------------------------------------------------------------------------------------------
    public GetLocalizationDictionary() {
        const res = {};

        if (!IsNullOrEmpty(this.Description.WidgetTitle))
            res[WidgetsThingsStructs.WidgetLocalizationKeys.Title] = this.Description.WidgetTitle;

        _.forEach(this.WidgetData.FormSettings.Elements, (elem) => {
            // add Label
            res['element_' + elem.Key] = elem.Label;

            // add Placeholder (if any)
            if (!IsNullOrEmpty(elem.Placeholder))
                res['element_placeholder' + elem.Key] = elem.Placeholder;

            // add Tooltip (if any)
            if (!IsNullOrEmpty(elem.TooltipLabel))
                res['element_TooltipLabel' + elem.Key] = elem.TooltipLabel;

            // add DefaultValue (if any and element type is Markdown )
            if (!IsNullOrEmpty(elem.DefaultValue) && elem.Type == ElementType.Markdown)
                res['element_default_value' + elem.Key] = elem.DefaultValue;

            // add Validation error (if mandatory)
            if (elem.Mandatory)
                res['element_error_' + elem.Key] = getDefaultValidationMessage(elem.Type);
        });

        _.forEach(this.WidgetData.FormSettings.Buttons, (button) => {
            res['button_' + button.Key] = button.Label;
        });

        _.forEach(this.WidgetData.FormSettings.RadioButton, (radio) => {
            _.forEach(radio.buttons, (radioButton) => {
                res['radiobutton_' + radio.key + '_' + radioButton.key] = radioButton.label;
            });
        });

        _.forEach(this.WidgetData.FormSettings.SelectData, (select) => {
            _.forEach(select.data, (kvpair) => {
                res['selectdata_' + select.Key + '_' + kvpair.key] = kvpair.label;
            });
        });

        // todo: consider moving keys to 'primitive' localization group
        res['selectdata_selectall'] = SELECT_ALL_LABEL;
        return res;
    }
    // ------------------------------------------------------------------------------------------------
}
function getDefaultValidationMessage(type: ElementType) {
    return type == ElementType.Email ? 'Enter a valid e-mail.' : 'Fill this field.';
}

// =======================================================================================================